// AlphaSignal Subscription Tier Configuration
// Stripe Product and Price ID mappings
import { ROUTES } from '@/lib/routes';

// ─── Tier type ────────────────────────────────────────────────────
// Foundation is the entry tier. No free tier, no free trial.
export type SubscriptionTier =
  | "foundation"
  | "performance_core"
  | "performance_intelligence"
  | "enterprise";

// Subscription status as stored in organizations.subscription_status
export type SubscriptionStatus = "active" | "past_due" | "canceled" | "expired";

// ─── Centralized Entitlement Logic ───────────────────────────────

/**
 * Expired / missing-subscription orgs are now fully locked.
 * EXPIRED_FALLBACK_TIER intentionally null — gating code treats null as
 * "no access, redirect to billing".
 */
export const EXPIRED_FALLBACK_TIER: SubscriptionTier | null = null;

/**
 * Compute the effective entitlement tier based on stored tier + subscription status.
 * Returns null when the org has no active access (expired/canceled/past_due/missing).
 */
export function computeEffectiveTier(
  storedTier: SubscriptionTier | null | undefined,
  subscriptionStatus: SubscriptionStatus | string | null | undefined,
  isBeta: boolean,
): SubscriptionTier | null {
  if (isBeta) return "performance_intelligence";
  if (!storedTier) return EXPIRED_FALLBACK_TIER;
  const isActive = subscriptionStatus === "active";
  return isActive ? storedTier : EXPIRED_FALLBACK_TIER;
}

/**
 * Determine access flags from subscription status.
 */
export function computeAccessFlags(
  subscriptionStatus: SubscriptionStatus | string | null | undefined,
  isBeta: boolean,
) {
  return {
    hasAccess: isBeta || subscriptionStatus === "active",
    isPaid: subscriptionStatus === "active" && !isBeta,
  };
}

// ─── Tier Configuration ──────────────────────────────────────────

export const TIER_CONFIG: Record<
  SubscriptionTier,
  {
    name: string;
    price: number;
    currency: string;
    priceId: string;
    productId: string;
    maxTeams: number;
  }
> = {
  foundation: {
    name: "Foundation",
    price: 499,
    currency: "EUR",
    priceId: "price_1T8jPPD34exnjq2uYc49BY2B",
    productId: "prod_U6xKe0YydX8cfD",
    maxTeams: 1,
  },
  performance_core: {
    name: "Performance Core",
    price: 999,
    currency: "EUR",
    priceId: "price_1THSWhD34exnjq2u6ppwbUYH",
    productId: "prod_UFyTV7RMOdhaA0",
    maxTeams: 2,
  },
  performance_intelligence: {
    name: "Performance Intelligence",
    price: 1499,
    currency: "EUR",
    priceId: "price_1THSWiD34exnjq2uN7qoVZlO",
    productId: "prod_UFyT1n8zkxgtx0",
    maxTeams: 3,
  },
  enterprise: {
    name: "Enterprise",
    price: 0,
    currency: "EUR",
    priceId: "",
    productId: "",
    maxTeams: 999,
  },
};

/** Get display config for any tier (null falls back to Foundation labels). */
export function getTierDisplayConfig(tier: SubscriptionTier | null | undefined) {
  return TIER_CONFIG[tier ?? "foundation"];
}

// ─── Shared Tier Resolver ────────────────────────────────────────
function resolveTier(tier: SubscriptionTier | null | undefined): SubscriptionTier {
  return tier ?? "foundation";
}

// Role availability by tier
export const TIER_ROLES: Record<SubscriptionTier, readonly string[]> = {
  foundation: ["org_admin", "performance", "coach", "operations", "player_viewer", "parent"] as const,
  performance_core: ["org_admin", "performance", "coach", "medical", "nutritionist", "physio", "operations", "player_viewer", "parent"] as const,
  performance_intelligence: ["org_admin", "performance", "coach", "medical", "nutritionist", "physio", "analyst", "operations", "player_viewer", "parent"] as const,
  enterprise: ["org_admin", "performance", "coach", "medical", "nutritionist", "physio", "analyst", "operations", "player_viewer", "parent"] as const,
};

// Max org_admin users by tier
export const TIER_MAX_ORG_ADMINS: Record<SubscriptionTier, number> = {
  foundation: 2,
  performance_core: 3,
  performance_intelligence: 5,
  enterprise: 999,
};

// Max total members (all roles) by tier — MUST match DB check_seat_limits
export const TIER_MAX_TOTAL_MEMBERS: Record<SubscriptionTier, number> = {
  foundation: 60,
  performance_core: 120,
  performance_intelligence: 150,
  enterprise: 999,
};

// Max staff members by tier — MUST match DB check_seat_limits
export const TIER_MAX_STAFF_MEMBERS: Record<SubscriptionTier, number> = {
  foundation: 10,
  performance_core: 30,
  performance_intelligence: 40,
  enterprise: 999,
};

// ─── Seat-limit helpers ──────────────────────────────────────────

export interface SeatCheckResult {
  allowed: boolean;
  reasonCode?: "seat_limit" | "tier_required";
  message?: string;
  requiredTier?: SubscriptionTier;
  currentCount?: number;
  maxAllowed?: number;
}

const STAFF_ROLES = ["org_admin", "performance", "coach", "medical", "nutritionist", "physio", "analyst", "operations"] as const;

export function isStaffRole(role: string): boolean {
  return (STAFF_ROLES as readonly string[]).includes(role);
}

export function canInviteMoreMembers(
  tier: SubscriptionTier | null | undefined,
  currentCounts: { totalMembers: number; staffMembers: number },
  targetRole: string,
): SeatCheckResult {
  const resolved = resolveTier(tier);

  if (!isRoleAllowedForTier(tier, targetRole)) {
    return {
      allowed: false,
      reasonCode: "tier_required",
      message: `The ${targetRole} role requires a higher plan.`,
      requiredTier: getMinimumTierForRole(targetRole),
    };
  }

  const maxTotal = TIER_MAX_TOTAL_MEMBERS[resolved];
  if (currentCounts.totalMembers >= maxTotal) {
    return {
      allowed: false,
      reasonCode: "seat_limit",
      message: `Your plan allows up to ${maxTotal} total members. You have ${currentCounts.totalMembers}.`,
      currentCount: currentCounts.totalMembers,
      maxAllowed: maxTotal,
      requiredTier: getNextTier(resolved),
    };
  }

  if (isStaffRole(targetRole)) {
    const maxStaff = TIER_MAX_STAFF_MEMBERS[resolved];
    if (currentCounts.staffMembers >= maxStaff) {
      return {
        allowed: false,
        reasonCode: "seat_limit",
        message: `Your plan allows up to ${maxStaff} staff members. You have ${currentCounts.staffMembers}.`,
        currentCount: currentCounts.staffMembers,
        maxAllowed: maxStaff,
        requiredTier: getNextTier(resolved),
      };
    }
  }

  return { allowed: true };
}

export function canAssignRole(
  tier: SubscriptionTier | null | undefined,
  targetRole: string,
  currentCounts: { staffMembers: number; orgAdminCount: number },
  currentRoleIsStaff: boolean,
): SeatCheckResult {
  const resolved = resolveTier(tier);

  if (!isRoleAllowedForTier(tier, targetRole)) {
    return {
      allowed: false,
      reasonCode: "tier_required",
      message: `The ${targetRole} role requires a higher plan.`,
      requiredTier: getMinimumTierForRole(targetRole),
    };
  }

  if (isStaffRole(targetRole) && !currentRoleIsStaff) {
    const maxStaff = TIER_MAX_STAFF_MEMBERS[resolved];
    if (currentCounts.staffMembers >= maxStaff) {
      return {
        allowed: false,
        reasonCode: "seat_limit",
        message: `Your plan allows up to ${maxStaff} staff members. You have ${currentCounts.staffMembers}.`,
        currentCount: currentCounts.staffMembers,
        maxAllowed: maxStaff,
        requiredTier: getNextTier(resolved),
      };
    }
  }

  if (targetRole === "org_admin") {
    const maxAdmins = TIER_MAX_ORG_ADMINS[resolved];
    if (currentCounts.orgAdminCount >= maxAdmins) {
      return {
        allowed: false,
        reasonCode: "seat_limit",
        message: `Your plan allows up to ${maxAdmins} org admin(s). You have ${currentCounts.orgAdminCount}.`,
        currentCount: currentCounts.orgAdminCount,
        maxAllowed: maxAdmins,
        requiredTier: getNextTier(resolved),
      };
    }
  }

  return { allowed: true };
}

export function canPromoteToOrgAdmin(
  tier: SubscriptionTier | null | undefined,
  currentOrgAdminCount: number,
): SeatCheckResult {
  const resolved = resolveTier(tier);
  const maxAdmins = TIER_MAX_ORG_ADMINS[resolved];
  if (currentOrgAdminCount >= maxAdmins) {
    return {
      allowed: false,
      reasonCode: "seat_limit",
      message: `Your plan allows up to ${maxAdmins} org admin(s). You have ${currentOrgAdminCount}.`,
      currentCount: currentOrgAdminCount,
      maxAllowed: maxAdmins,
      requiredTier: getNextTier(resolved),
    };
  }
  return { allowed: true };
}

const TIER_ORDER: SubscriptionTier[] = [
  "foundation",
  "performance_core",
  "performance_intelligence",
  "enterprise",
];

function getNextTier(current: SubscriptionTier): SubscriptionTier {
  const idx = TIER_ORDER.indexOf(current);
  return idx < TIER_ORDER.length - 1 ? TIER_ORDER[idx + 1] : current;
}

/**
 * Derive the minimum tier that includes a given role, based on TIER_ROLES.
 */
export function getMinimumTierForRole(role: string): SubscriptionTier {
  if (role === "super_admin") return "foundation";
  if (role === "parent") return "foundation";
  for (const tier of TIER_ORDER) {
    if (TIER_ROLES[tier].includes(role)) return tier;
  }
  return "performance_intelligence";
}

// ─── Feature flags ───────────────────────────────────────────────

export type FeatureFlag =
  // Foundation baseline
  | "team_messaging"
  | "availability_tracking"
  | "basic_player_management"
  | "basic_match_scheduling"
  | "basic_injury_tracking"
  | "player_roster_import"
  | "club_calendar"
  | "fee_collection"
  | "drill_library"
  | "basic_wellness_tracking"
  | "compliance_tracker"
  | "org_risk_insights"
  | "csv_uploads"
  | "opposition_analysis"
  | "gps_analytics"
  | "workload_analytics"
  | "acwr"
  | "monotony"
  | "strain"
  | "wellness_tracking"
  | "player_signal_score"
  | "video_analysis"
  | "player_recruitment"
  | "historical_import"
  | "venues"
  | "guardrails_management"
  | "session_planner"
  | "match_congestion_management"
  | "multi_season_trends"
  | "performance_lab"
  | "rules_based_digest"
  | "add_drill_to_session"
  | "club_registrations"
  | "club_public_page"
  // Performance Core
  | "medical_role"
  | "asymmetry_detection"
  | "governance_audit_log"
  | "travel_jet_lag"
  | "return_to_play"
  | "rtp_workflows"
  | "full_org_access_controls"
  // Performance Intelligence (AI-powered)
  | "readiness_forecasting"
  | "ai_insights_hub"
  | "scenario_planner"
  | "squad_gap_analysis"
  | "advanced_forecasting"
  | "weekly_ai_summary"
  | "lab_multi_scenario"
  | "lab_load_simulation"
  | "compare_players"
  | "ai_rtp_advisor"
  | "ai_opposition_brief"
  | "strength_conditioning";

const FOUNDATION_FEATURES: FeatureFlag[] = [
  "basic_player_management",
  "basic_match_scheduling",
  "basic_injury_tracking",
  "team_messaging",
  "availability_tracking",
  "player_roster_import",
  "club_calendar",
  "drill_library",
  "fee_collection",
  "basic_wellness_tracking",
  "compliance_tracker",
  "org_risk_insights",
  "club_registrations",
  "club_public_page",
  "csv_uploads",
  "opposition_analysis",
  "gps_analytics",
  "workload_analytics",
  "acwr",
  "monotony",
  "strain",
  "wellness_tracking",
  "player_signal_score",
  "video_analysis",
  "player_recruitment",
  "historical_import",
  "venues",
  "guardrails_management",
  "session_planner",
  "match_congestion_management",
  "multi_season_trends",
  "add_drill_to_session",
  "rules_based_digest",
  "performance_lab",
  "strength_conditioning",
];

const PERFORMANCE_CORE_FEATURES: FeatureFlag[] = [
  ...FOUNDATION_FEATURES.filter(
    (f) => f !== "fee_collection" && f !== "club_registrations",
  ),
  "medical_role",
  "asymmetry_detection",
  "governance_audit_log",
  "travel_jet_lag",
  "return_to_play",
  "rtp_workflows",
];

const PERFORMANCE_INTELLIGENCE_FEATURES: FeatureFlag[] = [
  ...PERFORMANCE_CORE_FEATURES,
  "readiness_forecasting",
  "ai_insights_hub",
  "scenario_planner",
  "squad_gap_analysis",
  "advanced_forecasting",
  "weekly_ai_summary",
  "compare_players",
  "ai_rtp_advisor",
  "ai_opposition_brief",
  "lab_multi_scenario",
  "lab_load_simulation",
];

const ENTERPRISE_FEATURES: FeatureFlag[] = [
  ...PERFORMANCE_INTELLIGENCE_FEATURES,
  "full_org_access_controls",
];

export const TIER_FEATURES: Record<SubscriptionTier, FeatureFlag[]> = {
  foundation: FOUNDATION_FEATURES,
  performance_core: PERFORMANCE_CORE_FEATURES,
  performance_intelligence: PERFORMANCE_INTELLIGENCE_FEATURES,
  enterprise: ENTERPRISE_FEATURES,
};

// ─── Helper functions ────────────────────────────────────────────

export function isRoleAllowedForTier(tier: SubscriptionTier | null | undefined, role: string): boolean {
  if (role === "super_admin") return true;
  if (!tier) return false;
  if (!TIER_ROLES[tier]) return false;
  return TIER_ROLES[tier].includes(role);
}

export function isFeatureEnabledForTier(tier: SubscriptionTier | null | undefined, feature: FeatureFlag): boolean {
  if (!tier) return false;
  if (!TIER_FEATURES[tier]) return false;
  return TIER_FEATURES[tier].includes(feature);
}

/**
 * Derive the minimum tier that includes a given feature, by scanning TIER_ORDER.
 */
export function getMinimumTierForFeature(feature: FeatureFlag): SubscriptionTier {
  for (const tier of TIER_ORDER) {
    if (TIER_FEATURES[tier]?.includes(feature)) return tier;
  }
  return "performance_intelligence";
}

export function getMaxOrgAdminsForTier(tier: SubscriptionTier | null | undefined): number {
  const resolved = resolveTier(tier);
  return TIER_MAX_ORG_ADMINS[resolved] ?? 1;
}

export function getTierFromPriceId(priceId: string): SubscriptionTier | null {
  const entry = Object.entries(TIER_CONFIG).find(([_, config]) => config.priceId === priceId);
  return entry ? (entry[0] as SubscriptionTier) : null;
}

export function getTierFromProductId(productId: string): SubscriptionTier | null {
  const entry = Object.entries(TIER_CONFIG).find(([_, config]) => config.productId === productId);
  return entry ? (entry[0] as SubscriptionTier) : null;
}

/** Get the numeric rank of a tier for comparison */
export function getTierRank(tier: SubscriptionTier | null | undefined): number {
  if (!tier) return -1;
  return TIER_ORDER.indexOf(tier);
}

// Route/page to feature mapping for access control
export const ROUTE_FEATURE_MAP: Record<string, FeatureFlag> = {
  [ROUTES.SCENARIO_PLANNER]: "scenario_planner",
  [ROUTES.RETURN_TO_PLAY]: "return_to_play",
  [ROUTES.RTP]: "rtp_workflows",
  [ROUTES.SQUAD_GAPS]: "squad_gap_analysis",
  [ROUTES.RISK_FORECAST]: "advanced_forecasting",
  [ROUTES.LEGACY_PLANNER]: "session_planner",
  [ROUTES.PLANNED_SESSIONS]: "session_planner",
  [ROUTES.SESSIONS]: "session_planner",
  [ROUTES.MATCHES]: "basic_match_scheduling",
  [ROUTES.MATCH_MODE_LEGACY]: "match_congestion_management",
  [ROUTES.AVAILABILITY]: "availability_tracking",
  [ROUTES.WELLNESS_SUMMARY]: "basic_wellness_tracking",
  [ROUTES.PLANNING]: "readiness_forecasting",
  // RECRUITMENT_OPPOSITION feature mapping removed — feature cut from product scope.
};
